import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, X, Smartphone } from "lucide-react";
import {
    enablePush,
    pushSupported,
    pushPermission,
    isSubscribed,
    dismissPushPrompt,
    isPushPromptDismissed,
} from "../lib/pushClient";

// Detect iOS Safari — web push requires the site to be added to Home Screen
// as a PWA before permission can be granted. We show a specific hint for
// those users instead of a broken "Allow" button.
const isIosSafari = () => {
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const isIos = /iPad|iPhone|iPod/.test(ua);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    const isStandalone =
        typeof window !== "undefined" && window.matchMedia("(display-mode: standalone)").matches;
    return isIos && isSafari && !isStandalone;
};

/**
 * Small dismissable banner nudging signed-in users to turn on push
 * notifications. Renders nothing when:
 *   - push isn't supported on the device,
 *   - permission was already granted (and we already have a live sub),
 *   - permission was denied at the browser level (nothing we can do),
 *   - or the user recently dismissed the prompt.
 */
export const PushPermissionBanner = () => {
    const [visible, setVisible] = useState(false);
    const [busy, setBusy] = useState(false);
    const [iosHint, setIosHint] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!pushSupported()) return;
            if (isPushPromptDismissed()) return;
            const perm = pushPermission();
            if (perm === "denied") return;
            if (perm === "granted") {
                // If they've granted permission but we don't have a stored
                // subscription (e.g. cleared site data), silently ignore —
                // Profile has an explicit toggle for re-subscribing.
                const sub = await isSubscribed();
                if (sub) return;
            }
            if (isIosSafari()) {
                setIosHint(true);
            }
            if (!cancelled) setVisible(true);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    if (!visible) return null;

    const handleEnable = async () => {
        try {
            setBusy(true);
            await enablePush();
            toast.success("Notifications turned on");
            setVisible(false);
        } catch (err) {
            const msg = err?.message || "";
            if (msg === "denied") {
                toast.error("Notifications blocked", {
                    description: "Enable them in your browser site settings.",
                });
                setVisible(false);
            } else if (msg === "dismissed") {
                // User closed the browser prompt without deciding — leave
                // the banner up so they can try again later.
            } else {
                toast.error("Couldn't turn on notifications", { description: msg });
            }
        } finally {
            setBusy(false);
        }
    };

    const handleDismiss = () => {
        dismissPushPrompt();
        setVisible(false);
    };

    return (
        <div
            data-testid="push-permission-banner"
            className="fixed bottom-4 left-1/2 z-50 w-[min(92vw,420px)] -translate-x-1/2 rounded-2xl border border-[#2e3220]/10 bg-[#F4F1EA] p-4 shadow-xl shadow-[#2e3220]/15"
        >
            <button
                type="button"
                data-testid="push-banner-close"
                onClick={handleDismiss}
                aria-label="Dismiss"
                className="absolute right-2 top-2 rounded-full p-1 text-[#5a6142] hover:bg-[#2e3220]/5"
            >
                <X className="h-4 w-4" strokeWidth={2.5} />
            </button>
            <div className="flex items-start gap-3 pr-6">
                <span className="mt-0.5 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-[#737a3d] text-[#F4F1EA]">
                    <Bell className="h-4 w-4" strokeWidth={2.5} />
                </span>
                <div className="min-w-0">
                    <p className="font-display text-sm font-bold text-[#2e3220]">
                        Get notified about rental updates
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-[#5a6142]">
                        Turn on push notifications and we'll ping you when someone requests
                        your item or when your payment goes through.
                    </p>
                    {iosHint && (
                        <p className="mt-2 inline-flex items-center gap-1 rounded-lg bg-[#B56C5A]/10 px-2 py-1 text-[11px] font-semibold text-[#B56C5A]">
                            <Smartphone className="h-3 w-3" strokeWidth={2.5} />
                            iPhone: tap Share → Add to Home Screen first
                        </p>
                    )}
                    <div className="mt-3 flex flex-wrap gap-2">
                        <button
                            type="button"
                            data-testid="push-banner-enable"
                            onClick={handleEnable}
                            disabled={busy}
                            className="inline-flex items-center gap-1.5 rounded-full bg-[#737a3d] px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-[#F4F1EA] hover:bg-[#5a6142] disabled:opacity-60"
                        >
                            {busy ? "Turning on…" : "Turn on"}
                        </button>
                        <button
                            type="button"
                            data-testid="push-banner-dismiss"
                            onClick={handleDismiss}
                            className="rounded-full px-3 py-1.5 text-xs font-semibold text-[#5a6142] hover:bg-[#2e3220]/5"
                        >
                            Not now
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PushPermissionBanner;
